import type { CategoryPageSideFilters } from '@c/CategoryOrSubcategoryPage';
import { useAuth } from 'context/AuthContext';
import { useBikeFilter } from 'context/BikeFilterContext';
import type { Make, UserBike } from 'models/shared';
import { useState } from 'react';
import Select from './Select';
import type { PageSideFilters } from '@util/hooks/useProductsPage';
import Button from './Button';
import { ChevronRightIcon, PlusIcon } from '@c/icons';
import Link from 'next/link';
import MobileSearchByVehicle from 'app/product/[slug]/components/SearchByVehicle/MobileSearchByVehicle';
import { RefreshCcwIcon } from 'lucide-react';
import { cn, isMobile } from '@util/index';
import DesktopSearchByVehicle from 'app/product/[slug]/components/SearchByVehicle/DesktopSearchByVehicle';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { logError } from '@util/logError';
import { addUserBike } from '@util/firestore/users';
import { useQueryClient } from '@tanstack/react-query';

export const ShopByDirtBike = ({
  onBikeComplete,
  sideFilters,
  hideYourSavedBikes,
}: {
  onBikeComplete: (bike: UserBike) => void;
  sideFilters: CategoryPageSideFilters;
  hideYourSavedBikes?: boolean;
}) => {
  const { userDoc } = useAuth();
  const [showMobileSearchByVehicle, setShowMobileSearchByVehicle] =
    useState(false);
  const [modalMode, setModalMode] = useState<'filter' | 'add-to-garage'>(
    'filter'
  );
  const queryClient = useQueryClient();

  const [showDesktopSearchByVehicle, setShowDesktopSearchByVehicle] =
    useState(false);

  async function addBikeToGarage(bike: Partial<UserBike>) {
    if (!userDoc) return;

    try {
      if (
        userDoc.bikes.findIndex(
          (b) =>
            b.model === bike.model &&
            b.year === bike.year &&
            b.make === bike.make
        ) === -1
      ) {
        await addUserBike(userDoc.uid, {
          ...(bike as UserBike), // passing bike as UserBike but is is actually a Partial<UserBike>
          date_added: Date.now(),
        });

        await queryClient.invalidateQueries({
          queryKey: ['authUser'],
        });

        onBikeComplete({
          ...(bike as UserBike),
        });
      }
    } catch (e) {
      logError(e);
    }
  }

  return (
    <div className="flex w-full flex-col justify-between gap-y-12 pr-0 sm:mx-auto sm:max-w-content sm:flex-row sm:gap-y-0 sm:pr-6">
      <div className="flex w-full flex-col gap-[1.6rem] sm:flex-row sm:items-center">
        <div className="hidden flex-col gap-2 whitespace-nowrap font-semibold sm:flex">
          <span className="text-[1.8rem]">Find Parts for Your Ride</span>

          {!userDoc && (
            <Link
              href={`/login`}
              className="flex items-center gap-2 whitespace-nowrap text-[1.5rem] text-brand-secondary hover:underline"
            >
              Log In to Add Your Ride
              <ChevronRightIcon className="h-8 w-8" />
            </Link>
          )}
        </div>

        <VehicleSelector
          fill={false}
          setShowMobileSearchByVehicle={setShowMobileSearchByVehicle}
          setShowDesktopSearchByVehicle={setShowDesktopSearchByVehicle}
          setModalMode={setModalMode}
        />
      </div>

      {!!userDoc?.bikes?.length && !hideYourSavedBikes ? (
        <div className="flex items-center gap-x-2">
          <div className="flex items-center justify-center gap-[1.6rem] rounded-2xl border-2 sm:min-w-[30rem]">
            <Select
              allowEmpty={false}
              key={`${sideFilters.bikeYears?.[0]} ${
                sideFilters.bikeMakes?.[0] ?? ''
              } ${sideFilters.bikeModels?.[0] ?? ''}`}
              placeholder="Your saved rides"
              grow
              options={
                userDoc?.bikes?.length
                  ? userDoc?.bikes.map((bike, i) => ({
                      label: `${bike.year} ${bike.make} ${bike.model}`,
                      value: bike,
                      id: `${bike.year}-${bike.make}-${bike.model}-${bike.type}`,
                    }))
                  : [
                      {
                        label: 'No bikes saved for this ride type',
                        value: 'No bikes saved for this ride type',
                        id: 'No bikes saved for this ride type',
                      },
                    ]
              }
              value={`${sideFilters.bikeYears?.[0] ?? ''}-${
                sideFilters.bikeMakes?.[0] ?? ''
              }-${sideFilters.bikeModels?.[0] ?? ''}-${
                sideFilters.rideTypes?.[0] ?? ''
              }`}
              onChange={(e) => {
                const bike = e.value;

                onBikeComplete(bike);

                if (bike?.type) {
                  sideFilters.rideTypes = [bike.type];
                }
              }}
              className="!rounded-[2rem]"
            />
          </div>

          <button
            type="button"
            className="duration-125 flex h-[4rem] w-[4rem] cursor-pointer items-center justify-center rounded-2xl bg-brand-secondary transition-all hover:bg-brand-secondary-darker"
            onClick={() => {
              setModalMode('add-to-garage');

              if (isMobile()) {
                setShowMobileSearchByVehicle(true);
              } else {
                setShowDesktopSearchByVehicle(true);
              }
            }}
          >
            <div className="flex h-[2rem] w-[2rem] items-center justify-center rounded-full bg-white">
              <PlusIcon className="text-brand-secondary" />
            </div>
          </button>
        </div>
      ) : (
        <>
          {!!userDoc && !userDoc?.bikes?.length && (
            <div className="flex items-center justify-center gap-[1.6rem]">
              <Button type="secondary" text="Add a Bike" />
            </div>
          )}
        </>
      )}

      <MobileSearchByVehicle
        show={showMobileSearchByVehicle}
        dismiss={() => setShowMobileSearchByVehicle(false)}
        onBikeComplete={
          modalMode === 'filter' ? onBikeComplete : addBikeToGarage
        }
        variant={modalMode}
      />

      <DesktopSearchByVehicle
        isOpen={showDesktopSearchByVehicle}
        dismiss={() => setShowDesktopSearchByVehicle(false)}
        onBikeComplete={
          modalMode === 'filter' ? onBikeComplete : addBikeToGarage
        }
        variant={modalMode}
      />
    </div>
  );
};

export const VehicleSelector = ({
  fill = true,
  setShowMobileSearchByVehicle,
  setShowDesktopSearchByVehicle,
  setModalMode,
  onBikeComplete,
}: {
  fill?: boolean;
  setShowMobileSearchByVehicle: (show: boolean) => void;
  setShowDesktopSearchByVehicle: (show: boolean) => void;
  setModalMode?: (mode: 'add-to-garage' | 'filter') => void;
  onBikeComplete?: (bike: UserBike) => void;
}) => {
  const { selectedBike } = useBikeFilter();

  const hasSelectedBike =
    selectedBike.year && selectedBike.make && selectedBike.model;

  return (
    <div className="flex h-full w-full items-center justify-end gap-x-8">
      <div className="hidden h-[4.5rem] w-[1.2px] bg-[#D9D9D9] sm:flex" />

      <button
        type="button"
        className={`mr-4 flex h-[5rem] w-[20rem] items-center justify-between rounded-[1.5rem] px-[2.4rem] py-[1rem] text-[1.6rem] font-medium text-zinc-800 ${
          fill
            ? 'w-full bg-[#F1F1F1]'
            : 'w-[30rem] border border-zinc-200 bg-brand-darker-white'
        }`}
        onClick={() => {
          if (isMobile()) {
            setModalMode?.('filter');
            setShowMobileSearchByVehicle(true);
          } else {
            setModalMode?.('filter');
            setShowDesktopSearchByVehicle(true);
          }
        }}
      >
        <span className="truncate">
          {hasSelectedBike
            ? `${selectedBike.year} ${selectedBike.make} ${selectedBike.model}`
            : 'Filter by vehicle'}
        </span>

        <MagnifyingGlassIcon className="h-8 w-8 text-zinc-700" />
      </button>
    </div>
  );
};

export const ShopByVehicle = ({
  sideFilters,
  onBikeComplete,
}: {
  sideFilters: PageSideFilters;
  onBikeComplete: (bike: UserBike) => void;
}) => {
  const { selectedBike } = useBikeFilter();
  const [showMobileSearchByVehicle, setShowMobileSearchByVehicle] =
    useState(false);

  const hasSelectedBike =
    selectedBike.year && selectedBike.make && selectedBike.model;

  const { userDoc } = useAuth();

  return (
    <>
      <div className="hidden w-full flex-col gap-[1.6rem] bg-[#edededfe] px-4 py-4 sm:flex sm:flex-row sm:items-center sm:rounded-[1.2rem] sm:px-8">
        <ShopByDirtBike
          sideFilters={sideFilters}
          onBikeComplete={onBikeComplete}
        />
      </div>

      <div className="mb-2 w-full sm:mb-8 sm:hidden">
        <div className="flex w-full flex-col items-start justify-between bg-[#F6F6F6] px-[2rem] sm:py-[1.5rem]">
          <div className="flex w-full items-center justify-between">
            <span className="text-[1.6rem] font-semibold text-black sm:text-[2rem]">
              {hasSelectedBike
                ? `Showing parts for ${selectedBike.year} ${selectedBike.make} ${selectedBike.model}`
                : 'Filter by Vehicle'}
            </span>

            {!hasSelectedBike && (
              <button
                className={cn(
                  'flex items-center justify-center gap-x-2 rounded-[1.5rem] px-8 py-4 text-[1.6rem] font-semibold sm:text-[1.8rem]',
                  !userDoc?.bikes.length
                    ? 'bg-brand-secondary text-white'
                    : 'text-brand-secondary'
                )}
                type="button"
                onClick={() => {
                  setShowMobileSearchByVehicle(true);
                }}
              >
                Add
                <PlusIcon
                  className={cn(
                    'h-7 w-7',
                    !userDoc?.bikes.length
                      ? 'text-white'
                      : 'text-brand-secondary'
                  )}
                />
              </button>
            )}
          </div>

          {hasSelectedBike && (
            <div className="mt-6 flex w-full justify-end">
              <button
                className="mt-4 flex items-center justify-center gap-x-4 rounded-[1rem] px-6 py-4 text-[1.8rem] font-semibold text-brand-secondary"
                type="button"
                onClick={() => {
                  setShowMobileSearchByVehicle(true);
                }}
              >
                <RefreshCcwIcon className="h-10 w-10 text-brand-secondary" />
                Select another bike
              </button>
            </div>
          )}
        </div>
      </div>

      <MobileSearchByVehicle
        show={showMobileSearchByVehicle}
        dismiss={() => setShowMobileSearchByVehicle(false)}
        onBikeComplete={onBikeComplete}
      />
    </>
  );
};
